import { signal } from "@preact/signals-react"
import { useEffect } from "react"
import { nanoid } from "nanoid"

import { addNotification } from "../stores/notification-store"
import { fetchComponentProfile } from "../utils/api"
import { NOT_FOUND } from "../utils/constants"

export const profile = signal({})
export const error = signal(null)
export const loading = signal(false)
export const isLoaded = signal(false)
export const modified = signal(false) // allows a component to flag the data as modified so subsequent calls to the hook will reload it
export const ccschema = signal(null)
export const parameterHash = signal(null)

const fetchData = async (businessEntityId, profileId, config, accessToken) => {

    const hash = await import('object-hash').then(module => module.default || module)
    // if any parameters have changed then flag as modified so that the hook will reload the data
    const currentParametersHash = hash([businessEntityId, profileId, config, accessToken])
    if (currentParametersHash != parameterHash.value) {
        modified.value = true
        parameterHash.value = currentParametersHash
    }

    // short-circuit if we have a pending request, are waiting for parameters or have already received an error
    if (loading.value || !businessEntityId || !profileId || !config || !accessToken || error.value || !modified.value) {
        return
    }

    // short-circuit if we already have the data and it's not flagged as modified
    const componentProfile = profile.value?.componentProfile
    if (componentProfile && componentProfile.id == profileId && !modified.value) {
        return
    }
    
    try {
        loading.value = true
        profile.value = await fetchComponentProfile(businessEntityId, profileId, config, accessToken)
        ccschema.value = profile.value?.componentProfile?.config?.utgGlobal?.ccschema ? profile.value.componentProfile.config.utgGlobal.ccschema : NOT_FOUND
        
        // api interfaces need an id assigned
        let apiInterfaces = profile.value.componentProfile.config.apiInterfaceConfig.apiInterface
        apiInterfaces.map((apiInterface) => {
            apiInterface.ccid = apiInterface.ccid ? apiInterface.ccid : nanoid()
        })

        isLoaded.value = true
    }
    catch(err) {
        error.value = err
        console.log(err)
        addNotification(`Unable to load profile due to the following error - ${err}`)
    }
    finally {
        loading.value = false
        modified.value = false
    }  
    
}

export default function useProfile(businessEntityId, profileId, config, accessToken) {
    useEffect(() => {
        fetchData(businessEntityId, profileId, config, accessToken)
    }, [businessEntityId, profileId, config, accessToken])

    return {
        profile, loading, isLoaded, error, modified, ccschema
    }
}